/**
 * 計算式の中の比較を取り出す（#3358, #3412）。
 *
 * `IF({合計金額} >= 100000, "高額", IF({合計金額} > 0, "通常", "要確認"))` のように
 * 比較がいくつあっても、それぞれ「何と何を比べたか」を内訳に出せるようにする。
 *
 * 比較の片側に計算が入る式（`{A} > {B} + 100`）は、式の再構築が必要になり
 * 「内訳は参照先の値を並べるだけ」という方針から外れるため取り出さない。
 */
import { calculationFunction } from '../calculation/constants';
import { tokenize, type Token } from '../calculation/tokenizer';
import { parseNumericValue } from './calculationNumeric';

/** 比較演算子。2文字のものを先に並べる（<= を < と = に割らないため） */
const COMPARISON_OPERATOR_LIST = ['<=', '>=', '!=', '=', '<', '>'];

/** 定数の前に付けられる符号 */
const SIGN_LIST = ['+', '-'];

/**
 * 比較の片側。
 * - field: 項目参照（`{合計金額}` / `SUM({明細.金額})`）
 * - literal: 定数。isText は `"1"` のように引用符で書かれた文字列か
 */
export type ComparisonTerm =
  | { kind: 'field'; fieldCode: string }
  | { kind: 'literal'; value: string; isText: boolean };

/** 式の中の比較1つ。左右は式に書かれた順 */
export interface ComparisonPart {
  left: ComparisonTerm;
  right: ComparisonTerm;
}

/**
 * 比較の片側を項目参照か定数として読む。
 * `{合計金額} * 2` のように計算が入ると、内訳の値と比べた値が食い違うため undefined。
 */
const toTerm = (side: Token[]): ComparisonTerm | undefined => {
  if (side.length === 1) {
    const [token] = side;
    if (token.type === 'fieldRef') return { kind: 'field', fieldCode: token.value };
    if (token.type === 'number' || token.type === 'string') {
      return { kind: 'literal', value: token.value, isText: token.type === 'string' };
    }
    return undefined;
  }
  // 符号付きの数値（{残高} < -100）
  const [sign, num] = side;
  if (side.length === 2 && sign.type === 'operator' && SIGN_LIST.includes(sign.value)) {
    if (num.type === 'number') {
      return { kind: 'literal', value: `${sign.value}${num.value}`, isText: false };
    }
    return undefined;
  }
  const [func, lparen, ref, rparen] = side;
  const isSum =
    side.length === 4 &&
    func.type === 'funcName' &&
    func.value.toUpperCase() === calculationFunction.sum &&
    lparen.type === 'lparen' &&
    ref.type === 'fieldRef' &&
    rparen.type === 'rparen';
  return isSum ? { kind: 'field', fieldCode: ref.value } : undefined;
};

/**
 * 比較演算子の左右を、同じ深さの `(` `)` `,` まで取り出す。
 * `IF(SUM({明細.金額}) = {合計金額}, …)` から条件部分だけを取り出すため。
 * 比較は優先順位がいちばん低いため、この範囲がそのまま比較の左辺・右辺になる。
 */
const comparisonSides = (tokens: Token[], index: number): [Token[], Token[]] => {
  let begin = index;
  let depth = 0;
  while (begin > 0) {
    const token = tokens[begin - 1];
    if (token.type === 'rparen') depth++;
    if (token.type === 'lparen') {
      if (depth === 0) break;
      depth--;
    }
    if (token.type === 'comma' && depth === 0) break;
    begin--;
  }

  let end = index + 1;
  depth = 0;
  while (end < tokens.length) {
    const token = tokens[end];
    if (token.type === 'lparen') depth++;
    if (token.type === 'rparen') {
      if (depth === 0) break;
      depth--;
    }
    if (token.type === 'comma' && depth === 0) break;
    end++;
  }

  return [tokens.slice(begin, index), tokens.slice(index + 1, end)];
};

/**
 * 式の中の比較を、書かれた順にすべて取り出す。
 * 片側が項目参照・定数1つでない比較と、項目を参照しない比較（`1 = 1`）は除く。
 * 壊れた式は空配列。
 */
export const extractComparisons = (formula: string): ComparisonPart[] => {
  let tokens: Token[];
  try {
    tokens = tokenize(formula);
  } catch {
    return [];
  }

  return tokens.flatMap((token, index) => {
    if (token.type !== 'operator' || !COMPARISON_OPERATOR_LIST.includes(token.value)) return [];
    const [leftSide, rightSide] = comparisonSides(tokens, index);
    const left = toTerm(leftSide);
    const right = toTerm(rightSide);
    if (left === undefined || right === undefined) return [];
    if (left.kind !== 'field' && right.kind !== 'field') return [];
    return [{ left, right }];
  });
};

/**
 * 実際の値の関係を表す記号を返す。
 *
 * `=` と書いた式でも結果は不一致でありうるため、**式の演算子ではなく値**で決める。
 * 一致かどうかは計算エンジンと同じルールで見る（両辺が数値として読めれば数値として比べ、
 * そうでなければ文字列として比べる）。"01" = "1" はエンジンでも一致になる。
 * 数値同士のときだけ大小まで示し、それ以外は一致／不一致だけを示す。
 * equalityOnly は `"1"` のような文字列との比較。区分コードで「2 > 1」と出ると
 * 大小を比べているように見えるため、一致／不一致だけにする。
 */
export const relationSymbol = (left: string, right: string, equalityOnly: boolean): string => {
  const a = parseNumericValue(left);
  const b = parseNumericValue(right);
  if (a === undefined || b === undefined) return left === right ? '=' : '≠';

  if (a === b) return '=';
  if (equalityOnly) return '≠';
  // 「2,010,938 > 1000」のように関係が一目で分かる
  return a > b ? '>' : '<';
};
